<template>
  <div class="ib-wrap">
    <v-overlay absolute :value="effectiveLoading" :opacity="0.3" :z-index="4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-overlay>

    <!-- Pie + items breakdown. For a map selection these come from the backend
         selection breakdown (selectionStats, fetched by Home); otherwise from
         the global/filtered stats. -->
    <div v-if="items.length" class="bottom-wrap">
      <div class="pie-wrap">
        <pie-chart :items="items" />
      </div>

      <!-- Two explicit columns with a divider between them, laid out by flexbox
           (space-around). Items are split down the middle: the first half fills
           the left column, the rest the right, so they read top-to-bottom. -->
      <div class="items-breakdown">
        <div class="items-column">
          <item-breakdown-row
            v-for="(item, index) in columns[0]"
            :key="index"
            :item="item"
          />
        </div>
        <!-- Centre vertical divider, sitting between the two columns. -->
        <div class="items-divider"></div>
        <div class="items-column">
          <item-breakdown-row
            v-for="(item, index) in columns[1]"
            :key="index"
            :item="item"
          />
        </div>
      </div>
    </div>

    <!-- No items to break down (empty filter/selection result, or still
         loading). -->
    <div v-else class="empty">
      <v-img
        max-height="150"
        max-width="150"
        :src="require('@/assets/home/empty.svg')"
      />
      <span>Sorry, there is no data yet</span>
    </div>
  </div>
</template>
<script>
import PieChart from "./PieChart.vue";
import ItemBreakdownRow from "./ItemBreakdownRow.vue";
import { mapState } from "vuex";

export default {
  components: {
    PieChart,
    ItemBreakdownRow,
  },
  props: {
    // The stats object owned/fetched by Home; only total_items is used here.
    stats: {
      type: Object,
      default: () => ({ total_items: [] }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // Backend weight + per-item-type breakdown for the active map selection,
    // fetched/owned by Home. Used (instead of stats) while a selection is active.
    selectionStats: {
      type: Object,
      default: null,
    },
    selectionStatsLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(["selection"]),
    // While a map selection is active the breakdown comes from selectionStats;
    // otherwise from the global/filtered stats.
    items() {
      const source = this.selection ? this.selectionStats : this.stats;
      return (source && source.total_items) || [];
    },
    effectiveLoading() {
      return this.selection ? this.selectionStatsLoading : this.loading;
    },
    // Split the list down the middle into [left, right] so the two columns read
    // top-to-bottom: first half (rounded up) on the left, the rest on the right.
    columns() {
      const half = Math.ceil(this.items.length / 2);
      return [this.items.slice(0, half), this.items.slice(half)];
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/styles/mixins.scss";
@import "@/styles/variables.scss";
.ib-wrap {
  position: relative;
  padding: 16px;
  min-width: 185px;
  // Fill the card so the pie can take a percentage of the real container height.
  height: 100%;
  display: flex;
  flex-direction: column;
  // Query container for the width-driven item columns below: 100cqi = this
  // panel's inner width. inline-size only so the pie's % height still works.
  container-type: inline-size;
}

// Top-level flexbox holding the pie chart and the items breakdown as two
// distinct elements, distributed with space-around.
.bottom-wrap {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  min-height: 0;
}

// Pie height = a percentage of the container; min-height keeps it from
// collapsing when the card is short. PieChart fills this wrapper.
.pie-wrap {
  height: 32%;
  min-height: 240px;
  flex: 0 0 auto;
}

// Wide panels (>= 700px, measured on the panel itself via the .ib-wrap container
// query — not the viewport): lay the pie and the items breakdown side by side in
// one row instead of stacked. 700px is the single tunable for this breakpoint.
@container (min-width: 600px) {
  .bottom-wrap {
    flex-direction: row;
    align-items: stretch;
  }
  .pie-wrap {
    // Fill the row height (share it with the list) rather than a % of the column.
    height: auto;
    flex: 1 1 0;
    min-width: 0;
    margin-left: -15px;
  }
  .items-breakdown {
    // A touch wider than the pie so it comfortably keeps its two columns.
    flex: 2 1 0;
    min-width: 0;
  }
}
// Two columns and a centre divider, laid out as a flex row with space-around
// so the columns get equal breathing room on either side of the divider.
.items-breakdown {
  display: flex;
  justify-content: space-around;
  align-items: stretch;
  // Cap the width so the two columns stay close together on wide panels rather
  // than drifting far apart.
  max-width: 500px;

  .items-column {
    // A real width (not just content-sized) so each row's v-spacer can push the
    // count to the right edge; shrinks on narrow panels rather than overflowing.
    flex: 0 1 200px;
    min-width: 0;
  }

  // Vertical divider between the two columns.
  .items-divider {
    flex: 0 0 1px;
    align-self: stretch;
    background-color: rgba(22, 50, 35, 0.1);
    margin: 0 12px;
  }
}
.theme--dark .items-divider {
  background-color: var(--v-divider-base);
}

.empty {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  span {
    font-size: 16px;
    margin-top: 10px;
    color: $color-text-muted;
    text-align: center;
  }
}
</style>
